import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { FileArrowUp } from "@phosphor-icons/react";

/**
 * Reusable bulk uploader with concurrent worker pool + persisted batch history.
 *
 * Props:
 *  - endpoint: backend POST URL (e.g. "/po/upload" or "/invoice/upload")
 *  - batchType: "po" | "invoice"  (stored in the persisted batch record)
 *  - concurrency: max parallel uploads (default 3)
 *  - title, accept, fileTypeHint, getNumberFromResponse, getRelatedIdFromResponse, getMatchedPoIdFromResponse
 *  - openHref: (item) => string  — link target for "Open" button in completed rows
 *  - onClose, onBatchSaved
 */
export default function BulkUploader({
  endpoint,
  batchType,
  concurrency = 3,
  title,
  accept = ".pdf,.rtf,.eml,application/pdf,application/rtf,message/rfc822",
  fileTypeHint = "Accepts: .pdf, .rtf, .eml — each file becomes a separate record via AI extraction",
  getNumberFromResponse,
  getRelatedIdFromResponse,
  getMatchedPoIdFromResponse,
  getChangeFromResponse,
  openHref,
  onClose,
  onBatchSaved,
}) {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [queue, setQueue] = useState([]); // [{ name, size, file, status, related_id, related_number, matched_po_id, error }]
  const [processing, setProcessing] = useState(false);
  const [startedAt, setStartedAt] = useState(null);

  const total = queue.length;
  const completed = queue.filter((q) => q.status === "done" || q.status === "failed").length;
  const succeeded = queue.filter((q) => q.status === "done").length;
  const failed = queue.filter((q) => q.status === "failed").length;
  const progressPct = total === 0 ? 0 : Math.round((completed / total) * 100);

  const updateAt = (i, patch) => setQueue((prev) => {
    const next = [...prev];
    next[i] = { ...next[i], ...patch };
    return next;
  });

  const startBulk = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const initial = files.map((f) => ({
      name: f.name, size: f.size, file: f, status: "pending",
    }));
    setQueue(initial);
    setProcessing(true);
    const t0 = Date.now();
    setStartedAt(t0);

    // Worker pool with concurrency cap
    let idx = 0;
    const workerCount = Math.min(concurrency, files.length);
    const localResults = [...initial];

    const worker = async () => {
      while (true) {
        const i = idx++;
        if (i >= files.length) return;
        updateAt(i, { status: "uploading" });
        const fd = new FormData();
        fd.append("file", files[i]);
        try {
          const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
          const number = getNumberFromResponse ? getNumberFromResponse(r.data) : undefined;
          const related_id = getRelatedIdFromResponse ? getRelatedIdFromResponse(r.data) : r.data.id;
          const matched_po_id = getMatchedPoIdFromResponse ? getMatchedPoIdFromResponse(r.data) : undefined;
          const change = getChangeFromResponse ? getChangeFromResponse(r.data) : undefined;
          const patch = { status: "done", related_id, related_number: number, matched_po_id, change };
          updateAt(i, patch);
          localResults[i] = { ...localResults[i], ...patch };
        } catch (e) {
          const error = e.response?.data?.detail || e.message || "Failed";
          updateAt(i, { status: "failed", error });
          localResults[i] = { ...localResults[i], status: "failed", error };
        }
      }
    };

    await Promise.all(Array.from({ length: workerCount }, worker));

    const duration = Date.now() - t0;
    const okCount = localResults.filter((x) => x.status === "done").length;
    const failCount = localResults.filter((x) => x.status === "failed").length;
    setProcessing(false);

    // Persist batch
    try {
      await api.post("/batches", {
        batch_type: batchType,
        total: files.length,
        succeeded: okCount,
        failed: failCount,
        concurrency: workerCount,
        duration_ms: duration,
        items: localResults.map((r) => ({
          filename: r.name,
          size: r.size,
          status: r.status,
          related_id: r.related_id,
          related_number: r.related_number,
          matched_po_id: r.matched_po_id,
          error: r.error,
        })),
      });
      onBatchSaved?.();
    } catch {
      /* don't block UX on batch persistence */
    }

    if (failCount === 0) toast.success(`${okCount} file${okCount === 1 ? "" : "s"} extracted in ${(duration / 1000).toFixed(1)}s`);
    else if (okCount === 0) toast.error(`All ${failCount} upload${failCount === 1 ? "" : "s"} failed`);
    else toast.success(`${okCount} succeeded · ${failCount} failed in ${(duration / 1000).toFixed(1)}s`);
  };

  const clearQueue = () => {
    if (processing) return;
    setQueue([]);
    setStartedAt(null);
  };

  return (
    <div className="jde-panel" style={{ marginTop: 12 }}>
      <div className="jde-panel-header">
        <span>{title}</span>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span className="muted" style={{ fontSize: 11 }}>Parallel: <strong>{concurrency}</strong></span>
          {queue.length > 0 && !processing && (
            <button className="jde-btn jde-btn-sm" onClick={clearQueue} data-testid="bulk-clear">Clear</button>
          )}
          <button className="jde-btn jde-btn-sm" onClick={onClose} data-testid="bulk-close" disabled={processing}>
            Close
          </button>
        </div>
      </div>
      <div className="jde-panel-body">
        <div
          data-testid="bulk-dropzone"
          onClick={() => !processing && ref.current?.click()}
          onDragOver={(e) => { e.preventDefault(); if (!processing) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragging(false);
            if (processing) return;
            startBulk(e.dataTransfer.files);
          }}
          style={{
            border: `2px dashed ${dragging ? "var(--omh-primary)" : "var(--omh-border-strong)"}`,
            background: dragging ? "#EFF6FF" : "var(--omh-bg-subtle)",
            padding: 36, textAlign: "center",
            cursor: processing ? "not-allowed" : "pointer",
            borderRadius: 8, opacity: processing ? 0.6 : 1,
          }}
        >
          <FileArrowUp size={28} weight="duotone" style={{ color: "var(--omh-primary)" }} />
          <div style={{ fontWeight: 700, marginTop: 6 }}>
            {processing
              ? `Extracting ${succeeded + failed} of ${total}… (${concurrency} in parallel)`
              : queue.length > 0
                ? "Drop more files or click to add"
                : "Click or drag files here — multiple supported"}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{fileTypeHint}</div>
        </div>
        <input
          ref={ref}
          type="file"
          multiple
          accept={accept}
          style={{ display: "none" }}
          data-testid="bulk-input"
          onChange={(e) => { startBulk(e.target.files); e.target.value = ""; }}
        />

        {queue.length > 0 && (
          <div style={{ marginTop: 16 }} data-testid="bulk-progress-panel">
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 6, fontSize: 12, fontWeight: 600,
            }}>
              <span>
                Progress: <span data-testid="bulk-progress-counts">{completed} / {total}</span>
                {succeeded > 0 && <span className="success-text" style={{ marginLeft: 10 }}>✓ {succeeded}</span>}
                {failed > 0 && <span className="alert-text" style={{ marginLeft: 10 }}>✗ {failed}</span>}
                {startedAt && !processing && (
                  <span className="muted" style={{ marginLeft: 10 }}>
                    · {((Date.now() - startedAt) / 1000).toFixed(1)}s
                  </span>
                )}
              </span>
              <span className="muted">{progressPct}%</span>
            </div>
            <div style={{
              height: 8, background: "var(--omh-bg-subtle)",
              borderRadius: 999, overflow: "hidden",
              border: "1px solid var(--omh-border)",
            }} data-testid="bulk-progress-bar">
              <div style={{
                width: `${progressPct}%`, height: "100%",
                background: failed > 0 && completed === total
                  ? "linear-gradient(90deg, var(--omh-success) 0%, var(--omh-warning) 100%)"
                  : "linear-gradient(90deg, var(--omh-cyan) 0%, var(--omh-primary) 100%)",
                transition: "width 0.25s ease",
              }} />
            </div>

            <div style={{
              marginTop: 12, maxHeight: 280, overflowY: "auto",
              border: "1px solid var(--omh-border)", borderRadius: 8,
            }}>
              {queue.map((q, i) => (
                <div
                  key={i}
                  data-testid={`bulk-row-${i}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto auto",
                    gap: 12, alignItems: "center",
                    padding: "8px 12px",
                    borderBottom: i < queue.length - 1 ? "1px solid var(--omh-divider)" : "none",
                    background: q.status === "uploading" ? "var(--omh-row-hover)" : undefined,
                  }}
                >
                  <span className="muted text-mono" style={{ fontSize: 11, width: 26 }}>{i + 1}.</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontWeight: 600, fontSize: 13,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{q.name}</div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {(q.size / 1024).toFixed(1)} KB
                      {q.related_number && <> · {q.related_number}</>}
                      {q.matched_po_id && q.related_number && <> → matched</>}
                      {q.change?.is_change && q.change?.change_of_po_id && <> · links to original</>}
                      {q.change?.is_change && !q.change?.change_of_po_id && q.change?.matches_sap_po && <> · matches SAP PO — review</>}
                      {q.error && <> · {q.error}</>}
                    </div>
                  </div>
                  <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {q.change?.is_change && (
                      <span className="jde-badge warning" data-testid={`bulk-change-${i}`} title="Detected as a Change / revision PO">CHANGE</span>
                    )}
                    {q.status === "pending" && <span className="jde-badge closed">Pending</span>}
                    {q.status === "uploading" && <span className="jde-badge confirmed">Extracting…</span>}
                    {q.status === "done" && <span className="jde-badge shipped">Done</span>}
                    {q.status === "failed" && <span className="jde-badge alert">Failed</span>}
                  </span>
                  {q.status === "done" && openHref && openHref(q) && (
                    <Link to={openHref(q)} className="jde-btn jde-btn-sm" data-testid={`bulk-open-${i}`}>Open</Link>
                  )}
                  {!(q.status === "done" && openHref && openHref(q)) && <span style={{ width: 1 }} />}
                </div>
              ))}
            </div>

            {!processing && completed === total && (
              <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  Batch complete. {succeeded} succeeded, {failed} failed.
                </span>
                <button className="jde-btn" onClick={() => { setQueue([]); onClose(); }} data-testid="bulk-finish">
                  Done
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
